/**
 * @retail-ops/api-client
 *
 * Root ApiClient composing all domain clients.
 *
 *   client.accounts.custSrch(...)
 *   client.jxrl.ofacSrch(...)
 *   client.signing.start(...)
 *   client.retail.carts.get(...)
 *   client.retail.config.products()
 *   client.retail.config.users.byEmail('x@y.com')
 *   client.portal.identity.resolve()
 */

import { ApiHttpClient } from './http.js';
import type { ApiHttpClientConfig } from './http.js';
import { AccountsApi } from './domains/accounts.js';
import { JxrlApi } from './domains/jxrl.js';
import { SigningApi, RetailSigningApi } from './domains/signing.js';
import { PdfApi } from './domains/pdf.js';
import { TemplatesApi } from './domains/templates.js';
import { UspsApi } from './domains/usps.js';
import { LegacyTasksApi } from './domains/tasks-legacy.js';
import { HealthApi } from './domains/health.js';
import { CartsApi } from './domains/carts.js';
import { DisclosuresApi } from './domains/disclosures.js';
import { SessionsApi } from './domains/sessions.js';
import { SessionApi } from './domains/session.js';
import { EventsApi } from './domains/events.js';
import { ToursApi } from './domains/tours.js';
import { ConfigChangesApi } from './domains/configChanges.js';
import { TasksApi } from './domains/tasks.js';
import { WeeklyTasksApi } from './domains/tasks-weekly.js';
import { KillListApi } from './domains/kill-list.js';
import { QcApi } from './domains/qc.js';
import { MetricsApi } from './domains/metrics.js';
import { StaffApi } from './domains/staff.js';
import { ConfigApi } from './domains/config.js';
import { AdminApi } from './domains/admin.js';
import { AdminProductsApi } from './domains/admin-products.js';
import { AdminComplianceApi } from './domains/admin-compliance.js';
import { AdminCartRetentionApi } from './domains/admin-cart-retention.js';
import { AdminDocumentRetentionApi } from './domains/admin-document-retention.js';
import { AdminPilotOfficersApi } from './domains/admin-pilot-officers.js';
import { AdminLnVerificationRulesApi } from './domains/admin-ln-verification-rules.js';
import { NotificationsApi } from './domains/notifications.js';
import { ComplianceApi } from './domains/compliance.js';
import { DocumentsApi } from './domains/documents.js';
import { IraApi } from './domains/ira.js';
// Wave 6.3 — Maintenance close-out + SuperAdmin records management.
import { MaintActionsApi } from './domains/maint-actions.js';
import { SuperAdminApi } from './domains/super-admin.js';
// WAVE-9 — Lobby Queue + Bookings + Approvals (independent v3 capabilities).
import { LobbyApi } from './domains/lobby.js';
import { BookingsApi } from './domains/bookings.js';
import { ApprovalsApi } from './domains/approvals.js';
// WAVE-11 — Fraud Case + Finance Forms intake + unified My Requests.
import { FraudApi } from './domains/fraud.js';
import { FinanceApi } from './domains/finance.js';
// WAVE-12 — RAS Reports + IRA Wave 5.5 + FollowUpTask (#10).
import { ReportsApi } from './domains/reports.js';
import { FollowUpTasksApi } from './domains/follow-up-tasks.js';
import { MaintenanceApi } from './domains/maintenance.js';
import { ProfileChangeApi } from './domains/profile-change.js';
import { DeathNoticeApi } from './domains/death-notice.js';
import { IraDisclosureBodiesApi } from './domains/ira-disclosure-bodies.js';
// v0.1.87 — NRA multi-document scanner + W-8BEN generation client.
import { NraApi } from './domains/nra.js';
// v0.14.17 — LexisNexis KBA + InstantID-search scaffold (defaults disabled).
import { LexisNexisApi, LexisNexisConfigAdminApi } from './domains/lexisnexis.js';
import { IdentityApi } from './domains/identity.js';
import { DevApi } from './domains/dev.js';
import { OfacApi } from './domains/ofac.js';
import { LegacyApplicationsApi } from './domains/legacy-applications.js';
import { OnlineApplicationsApi } from './domains/online-applications.js';
import { TasksCreateApi } from './domains/tasks-create.js';
import { PortalApi } from './domains/portal.js';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ApiClientConfig extends ApiHttpClientConfig {
  /** Alias used by teams-app-wiring §2.1 — maps to `functionKey`. */
  funcKey?: string;
  /** Alias used by teams-app-wiring — maps to `getToken`. */
  getAuthToken?: () => Promise<string> | string;
}

function normalizeConfig(cfg: ApiClientConfig): ApiHttpClientConfig {
  // Accept either {functionKey} or {funcKey}; either getToken or getAuthToken.
  const functionKey = cfg.functionKey ?? cfg.funcKey ?? '';
  const getToken = cfg.getToken ?? (cfg.getAuthToken
    ? async (): Promise<string | undefined> => {
        try {
          const t = await Promise.resolve(cfg.getAuthToken!());
          return t;
        } catch {
          return undefined;
        }
      }
    : undefined);

  return {
    baseUrl: cfg.baseUrl,
    functionKey,
    ...(getToken ? { getToken } : {}),
    ...(cfg.version ? { version: cfg.version } : {}),
    ...(cfg.onError ? { onError: cfg.onError } : {}),
    ...(cfg.context ? { context: cfg.context } : {}),
    ...(cfg.fetch ? { fetch: cfg.fetch } : {}),
    ...(cfg.logger ? { logger: cfg.logger } : {}),
  };
}

// ---------------------------------------------------------------------------
// V3 facade
// ---------------------------------------------------------------------------

export class ApiFacade {
  public readonly carts: CartsApi;
  /** POST /v3/disclosures/resolve|render — the server's document list + signer roles (wired 2026-09-30). */
  public readonly disclosures: DisclosuresApi;
  public readonly sessions: SessionsApi;
  public readonly session: SessionApi;
  public readonly events: EventsApi;
  /** /v3/me/tours — guided-tour progress (2026-10-07). */
  public readonly tours: ToursApi;
  /** /v3/admin/config-changes — configuration change history + restore (2026-10-08). */
  public readonly configChanges: ConfigChangesApi;
  public readonly tasks: TasksApi;
  public readonly killList: KillListApi;
  public readonly qc: QcApi;
  public readonly metrics: MetricsApi;
  public readonly staff: StaffApi;
  public readonly notifications: NotificationsApi;
  public readonly compliance: ComplianceApi;
  public readonly config: ConfigApi;
  public readonly admin: AdminApi;
  public readonly documents: DocumentsApi;
  public readonly ira: IraApi;
  /** Wave 6.3 — Account-Maintenance close-out + resolution surface. */
  public readonly maintActions: MaintActionsApi;
  /** Wave 6.3 — SuperAdmin Records Management surface. */
  public readonly superAdmin: SuperAdminApi;
  /** WAVE-9 — Lobby Queue (5 surfaces sharing one schema). */
  public readonly lobby: LobbyApi;
  /** WAVE-9 — Microsoft Bookings + Teams Virtual Appointments. */
  public readonly bookings: BookingsApi;
  /** WAVE-9 — Microsoft Graph Approvals proxy. */
  public readonly approvals: ApprovalsApi;
  /** WAVE-11 — Fraud Case intake (replaces legacy Canvas + SP-buffer + cloud flow). */
  public readonly fraud: FraudApi;
  /** WAVE-11 — Finance form intake (RDA/B2B/Dormant) + My Requests. */
  public readonly finance: FinanceApi;
  /** WAVE-12 — RAS Reports module (Audit A7). */
  public readonly reports: ReportsApi;
  /** WAVE-12 — Foreign + Deceased follow-up tasks (#10). */
  public readonly followUpTasks: FollowUpTasksApi;
  /** Account maintenance (Bill 2026-10-17). */
  public readonly maintenance: MaintenanceApi;
  /** BACKLOG-1675 — existing-customer profile change: preview (diff + account waterfall) and apply. */
  public readonly profileChange: ProfileChangeApi;
  /** Death Notice rebuild Wave 1 (locked 2026-04-27). Pilot-gated end-to-end. */
  public readonly deathNotice: DeathNoticeApi;
  /** v0.1.87 — NRA multi-document scanner + W-8BEN generation. */
  public readonly nra: NraApi;
  /** v0.14.17 — LexisNexis KBA + InstantID-search scaffold (defaults disabled). */
  public readonly lexisNexis: LexisNexisApi;
  /**
   * v3 Identity Verification Pipeline (Bill 2026-05-18, BACKLOG-0068).
   * Source-agnostic across html-import (Tier 1 bookmarklet), ddp-api
   * (Tier 3 LN DDP REST), and internal-pipeline (v3-native composite).
   */
  public readonly identity: IdentityApi;
  /**
   * Phase 1 (Bill 2026-05-10) — Internal click-to-sign + canvas + ESIGN
   * consent gate. Sub-namespaces: `.esign.*` (status/text/record) +
   * `.internal.*` (prepare/capture). Backed by /v3/esign/* + /v3/signing/
   * internal/*. Component-side imports happen through this facade.
   */
  public readonly signing: RetailSigningApi;

  /** Wave-3 (Bill 2026-05-09) — Dev-only tooling endpoints (SuperAdmin gated). */
  public readonly dev: DevApi;
  /** Wave-3 (Bill 2026-05-09) — OFAC archive-match classification. */
  public readonly ofac: OfacApi;
  /** Phase 1.0 #2 — legacy SharePoint application bridge. */
  public readonly legacyApplications: LegacyApplicationsApi;
  /** Phase 2 — PCB-Flow online-application (portal) bridge → preloaded cart. */
  public readonly onlineApplications: OnlineApplicationsApi;

  public constructor(http: ApiHttpClient) {
    this.carts = new CartsApi(http);
    this.disclosures = new DisclosuresApi(http);
    this.sessions = new SessionsApi(http);
    this.session = new SessionApi(http);
    this.events = new EventsApi(http);
    this.tours = new ToursApi(http);
    this.configChanges = new ConfigChangesApi(http);
    this.tasks = new TasksApi(http);
    // ── WEEKLY-TASKS-BUILD — augment TasksApi with .weekly ──────────
    (this.tasks as { weekly: WeeklyTasksApi }).weekly = new WeeklyTasksApi(http);
    this.killList = new KillListApi(http);
    this.qc = new QcApi(http);
    this.metrics = new MetricsApi(http);
    this.staff = new StaffApi(http);
    this.notifications = new NotificationsApi(http);
    this.compliance = new ComplianceApi(http);
    this.config = new ConfigApi(http);
    this.admin = new AdminApi(http);
    // Wave 6.1 WRITE-ROUTES-PRODUCTS — typed Products / Features /
    // CardTypes write surface attached to AdminApi via module
    // augmentation (see v3-admin-products.ts). The cast is required
    // because the augmented `products` property is `readonly`; we
    // assign once at construction here and never mutate again.
    (this.admin as { products: AdminProductsApi }).products = new AdminProductsApi(http);
    // Phase 2 admin migration — typed Compliance config write surface
    // (gates / citations / gate-keys / entity-rules) attached the same way
    // (module augmentation in v3-admin-compliance.ts).
    (this.admin as { compliance: AdminComplianceApi }).compliance = new AdminComplianceApi(http);
    // Wave 6.2 CART-RESOLUTION — typed CartRetentionPolicies admin surface
    // attached the same way as `products` (module augmentation in
    // v3-admin-cart-retention.ts).
    (this.admin as { cartRetentionPolicies: AdminCartRetentionApi }).cartRetentionPolicies =
      new AdminCartRetentionApi(http);
    // AO-DOCUMENTS-SCHEMA-REBUILD (2026-04-25) — typed DocumentRetentionPolicies
    // admin surface attached via the same module-augmentation pattern.
    (this.admin as { documentRetentionPolicies: AdminDocumentRetentionApi }).documentRetentionPolicies =
      new AdminDocumentRetentionApi(http);
    // PILOT-GATE-INFRA — typed PilotOfficers admin surface attached via the
    // same module-augmentation pattern as products + cartRetentionPolicies.
    (this.admin as { pilotOfficers: AdminPilotOfficersApi }).pilotOfficers =
      new AdminPilotOfficersApi(http);
    // BACKLOG-0501 — typed LN Verification Rules admin surface attached via
    // the same module-augmentation pattern. Edits the (cohort × citizenship
    // × tenure × channel) → mode matrix that drives the Verify-tab LN gate.
    (this.admin as { lnVerificationRules: AdminLnVerificationRulesApi }).lnVerificationRules =
      new AdminLnVerificationRulesApi(http);
    this.documents = new DocumentsApi(http);
    this.ira = new IraApi(http);
    this.maintActions = new MaintActionsApi(http);
    this.superAdmin = new SuperAdminApi(http);
    // ── WAVE-9 — Lobby + Bookings + Approvals ──────────────────────────
    this.lobby = new LobbyApi(http);
    this.bookings = new BookingsApi(http);
    this.approvals = new ApprovalsApi(http);
    // ── WAVE-11 — Fraud + Finance Forms + My Requests ──────────────────
    this.fraud = new FraudApi(http);
    this.finance = new FinanceApi(http);
    // ── WAVE-12 — RAS Reports + IRA Wave 5.5 + FollowUpTask ─────────────
    this.reports = new ReportsApi(http);
    this.followUpTasks = new FollowUpTasksApi(http);
    this.maintenance = new MaintenanceApi(http);
    this.profileChange = new ProfileChangeApi(http);
    this.deathNotice = new DeathNoticeApi(http);
    // v0.1.87 — NRA scanner + W-8BEN generation
    this.nra = new NraApi(http);
    // v0.14.17 — LexisNexis KBA + InstantID-search scaffold (defaults disabled).
    this.lexisNexis = new LexisNexisApi(http);
    // Bill 2026-05-18 — Identity Verification Pipeline (BACKLOG-0068).
    this.identity = new IdentityApi(http);
    // Augment AdminApi with `.lexisNexisConfig` (module augmentation pattern).
    (this.admin as { lexisNexisConfig: LexisNexisConfigAdminApi }).lexisNexisConfig =
      new LexisNexisConfigAdminApi(http);
    // Augment IraApi with `.disclosureBodies` (module augmentation pattern).
    (this.ira as { disclosureBodies: IraDisclosureBodiesApi }).disclosureBodies =
      new IraDisclosureBodiesApi(http);
    // Phase 1 (Bill 2026-05-10) — Internal signing primitives + ESIGN gate.
    this.signing = new RetailSigningApi(http);
    // Wave-3 (Bill 2026-05-09) — Dev tooling + OFAC classifier + legacy SP bridge.
    this.dev = new DevApi(http);
    this.ofac = new OfacApi(http);
    this.legacyApplications = new LegacyApplicationsApi(http);
    this.onlineApplications = new OnlineApplicationsApi(http);
    // Wave 14 — typed task-creation surface attached as `tasks.creation`.
    (this.tasks as { creation: TasksCreateApi }).creation = new TasksCreateApi(http);
  }
}

// ---------------------------------------------------------------------------
// ApiClient root
// ---------------------------------------------------------------------------

export class ApiClient {
  public readonly http: ApiHttpClient;

  public readonly accounts: AccountsApi;
  public readonly jxrl: JxrlApi;
  public readonly signing: SigningApi;
  public readonly pdf: PdfApi;
  public readonly templates: TemplatesApi;
  public readonly usps: UspsApi;
  public readonly tasks: LegacyTasksApi;
  public readonly health: HealthApi;
  public readonly retail: ApiFacade;
  public readonly portal: PortalApi;

  public constructor(cfg: ApiClientConfig) {
    this.http = new ApiHttpClient(normalizeConfig(cfg));
    this.accounts = new AccountsApi(this.http);
    this.jxrl = new JxrlApi(this.http);
    this.signing = new SigningApi(this.http);
    this.pdf = new PdfApi(this.http);
    this.templates = new TemplatesApi(this.http);
    this.usps = new UspsApi(this.http);
    this.tasks = new LegacyTasksApi(this.http);
    this.health = new HealthApi(this.http);
    this.retail = new ApiFacade(this.http);
    this.portal = new PortalApi(this.http);
  }
}
